import React from 'react'
import * as yup from 'yup'
import { Button, Grid, Card, Skeleton } from '@softwaredev_cegeka/stix-core'
import {
    Checkbox,
    Fieldset,
    FormGroup,
    Info,
    Input,
} from '@softwaredev_cegeka/stix-form'

import BaseForm from 'src/components/Forms/BaseForm'
import { authenticatedUserStore, notifierStore } from 'src/stores'
import { AuthApi } from 'src/api/authApi'

import './groupForm.scss'
import AsyncSelect from 'src/components/Forms/AsyncSelect'
import axios from 'axios'
import { observer } from 'mobx-react'

class UserForm extends BaseForm {
    checkIfCegekaUser = () => {
        if (this.props.selectedOrganization) {
            if (
                this.props.selectedOrganization?.value === '656' ||
                this.props.selectedOrganization?.value === '2315'
            ) {
                return true
            }
        } else {
            if (
                authenticatedUserStore.userData.organization_id === '656' ||
                authenticatedUserStore.userData.organization_id === '2315'
            ) {
                return true
            }
        }
        return false
    }

    checkIfTenantAdmin = async currentOrganizationId => {
        let isTenantAdmin = false
        try {
            const response = await this.authClient.fetchUsers(
                1,
                null,
                {
                    per_page: 1000,
                    user_is_admin: true,
                },
                currentOrganizationId
            )
            const tenantAdmins = response.data.results
            tenantAdmins.forEach(tenantAdmin => {
                if (
                    tenantAdmin.username ===
                    authenticatedUserStore.userData.username
                ) {
                    isTenantAdmin = true
                }
            })
            return isTenantAdmin
        } catch (e) {
            return isTenantAdmin
        }
    }

    state = {
        activePageNumber: 0,
        columnSizes: { md: 3 },
        errors: {},
        isSubmitting: false,
        submittedSuccessfully: false,
        userCreated: null,
        touched: {},
        values: {},
        uploadProgress: null,
        userIsTenantAdmin: false,
        loading: true,
    }

    pages = [
        {
            renderMethod: this.renderForm,
            validationSchema: yup.object().shape({
                lastName: yup.string().required('Please fill in the last name'),
                firstName: yup
                    .string()
                    .required('Please fill in the first name'),
                username: yup.mixed().test({
                    name: 'cegekaUser',
                    message: 'Please fill in the username',
                    test: value => {
                        if (this.checkIfCegekaUser()) {
                            return value.length > 0
                        }
                        return true
                    },
                }),
                tenant: yup.string(),
                email: yup
                    .string()
                    .email('Must be a valid email address')
                    .required('Please fill in the email'),
                password: yup.string(),
                repeatedPassword: yup.string().test({
                    name: 'passwordsMatch',
                    message: 'The passwords should match',
                    test: value => {
                        const { values } = this.state
                        return value === values.password
                    },
                }),
                localUser: yup.bool(),
                locked: yup.bool(),
                groups: yup
                    .array()
                    .of(
                        yup.object().shape({
                            label: yup.string(),
                            value: yup.number(),
                        })
                    )
                    .nullable(),
            }),
        },
    ]

    initialValues = {
        lastName: '',
        firstName: '',
        username: '',
        tenant: '',
        email: '',
        password: '',
        repeatedPassword: '',
        localUser: false,
        locked: false,
        groups: null,
        initialGroups: [],
    }

    authClient = new AuthApi(authenticatedUserStore.authToken)

    constructor(props, context) {
        super(props, context)
        this.state.values = { ...this.initialValues }
        if (props.match.params.username) {
            this.authClient
                .fetchUser(props.match.params.username)
                .then(response => {
                    this.initialValues = {
                        firstName: response.data.first_name,
                        lastName: response.data.last_name,
                        username: response.data.username,
                        email: response.data.email,
                        localUser: response.data.is_local_user,
                        locked: response.data.locked,
                        groups: response.data.groups.map(group => ({
                            id: group.id,
                            value: group.id,
                            label: group.name,
                        })),
                        initialGroups: response.data.groups.map(group => ({
                            id: group.id,
                            value: group.id,
                            label: group.name,
                        })),
                    }
                    this.setState({
                        values: { ...this.initialValues },
                        userCreated: response.data.id,
                    })
                })
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.organizationId !== this.props.organizationId) {
            this.checkIfTenantAdmin(this.props.organizationId).then(res => {
                this.setState({
                    userIsTenantAdmin: res,
                    loading: false,
                })
            })
        }
    }

    componentDidMount() {
        if (this.props.match.params.username) {
            this.setState({
                loading: false,
            })
        }
        if (this.props.organizationId && this.state.loading) {
            this.checkIfTenantAdmin(this.props.organizationId).then(res => {
                this.setState({
                    userIsTenantAdmin: res,
                    loading: false,
                })
            })
        }
    }

    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    handleGroupsRender = response => {
        return response.data.results.map(item => ({
            id: item.id,
            value: item.id,
            label: item.name,
        }))
    }

    getSubmitUrl() {
        if (this.props.match.params.username) {
            return `/api/auth/users/${this.props.match.params.username}`
        } else {
            return `/api/auth/users`
        }
    }

    getSubmitMethod() {
        if (this.props.match.params.username) {
            return 'PUT'
        } else {
            return 'POST'
        }
    }

    generatePayload = () => {
        return null
    }

    generateGeneralInfoPayload = () => {
        const values = this.state.values
        return {
            first_name: values.firstName,
            last_name: values.lastName,
            username: this.checkIfCegekaUser()
                ? values.username
                : values.email.toLowerCase(),
            email: values.email,
            password: values.password,
            local_user: values.localUser,
            locked: values.locked,
            tenant_uid: this.getUserOrganization(),
        }
    }

    submitGeneralInfo = async () => {
        let isValid = await this.validateForm()
        if (isValid) {
            const payload = this.generateGeneralInfoPayload()
            if (!payload) {
                console.warn('No data to submit')
                console.info('state values: ', this.state.values)
                return
            }
            notifierStore.setShowMessage(false)
            try {
                let resp = await axios({
                    url: this.getSubmitUrl(),
                    method: this.getSubmitMethod(),
                    data: payload,
                })
                if (resp.status === 201) {
                    notifierStore.setToastInfo(
                        this.props.match.params.username
                            ? 'User data successfully changed'
                            : 'User successfully created',
                        true
                    )
                    this.setState({
                        userCreated: resp.data.id,
                    })
                } else {
                    notifierStore.setToastInfo(
                        this.props.match.params.username
                            ? 'User data could not be updated'
                            : 'User could not be created',
                        false
                    )
                }
            } catch (ex) {
                console.warn('API Exception', ex)
                notifierStore.setToastInfo(
                    ex.response?.data?.error || ex.response?.data?.detail,
                    false
                )
            } finally {
                notifierStore.setShowMessage(true)
            }
        } else {
            this.activateFirstPageWithErrors()
        }
    }

    submitGroupInfo = async () => {
        let failed = []
        let groups = this.state.values.groups || []
        let initialGroups = this.state.values.initialGroups || []
        let removedGroups = initialGroups.filter(
            ({ value: id1 }) => !groups.some(({ value: id2 }) => id2 === id1)
        )
        let addedGroups = groups.filter(
            ({ value: id1 }) =>
                !initialGroups.some(({ value: id2 }) => id2 === id1)
        )
        if (removedGroups.length === 0 && addedGroups.length === 0) {
            notifierStore.setToastInfo('No changes to submit', false)
            notifierStore.setShowMessage(true)
        } else {
            for (let i = 0; i < addedGroups.length; i++) {
                try {
                    await this.authClient.addUserToGroup(
                        this.state.userCreated,
                        addedGroups[i].value
                    )
                } catch (e) {
                    failed.push(addedGroups[i].label)
                }
            }
            for (let i = 0; i < removedGroups.length; i++) {
                try {
                    await this.authClient.removeUserFromGroup(
                        this.state.userCreated,
                        removedGroups[i].value
                    )
                } catch (e) {
                    failed.push(removedGroups[i].label)
                }
            }
            notifierStore.setShowMessage(false)
            if (failed.length === 0) {
                notifierStore.setToastInfo(
                    'User membership successfully updated',
                    true
                )
                this.setState(
                    {
                        values: {
                            ...this.state.values,
                            initialGroups: groups,
                        },
                        submittedSuccessfully: true,
                    },
                    () => {
                        notifierStore.setShowMessage(true)
                    }
                )
            } else {
                notifierStore.setToastInfo(
                    'User membership successfully updated for the following groups: ' +
                        failed,
                    false
                )
                notifierStore.setShowMessage(true)
            }
        }
    }

    // overwrite this in order to render the buttons separately
    renderFormActions(pageNumber) {
        return null
    }

    getUserOrganization() {
        if (authenticatedUserStore.selectedOrganization) {
            return authenticatedUserStore.selectedOrganization.id
        } else {
            const tenants = authenticatedUserStore.userOrganizations
            for (let i = 0; i < tenants.length; i++) {
                if (
                    tenants[i].organization_id ===
                    authenticatedUserStore.userData.organization_id
                ) {
                    return tenants[i].id
                }
            }
        }
    }

    renderLoadingSkeleton() {
        return (
            <Card>
                <Card.Header>{<Skeleton />}</Card.Header>
                <Card.Body>{<Skeleton count={6} />}</Card.Body>
                <Card.Footer>{<Skeleton />}</Card.Footer>
            </Card>
        )
    }

    renderForm() {
        const isCegekaUser = this.checkIfCegekaUser()
        const { userIsGlobalAdmin } = this.props
        const { userIsTenantAdmin, userCreated, values } = this.state
        const disabledFields = userCreated && !this.props.match.params.username
        const userIsAdmin = userIsGlobalAdmin || userIsTenantAdmin
        const displayUsernameField =
            (userIsTenantAdmin &&
                isCegekaUser &&
                !this.props.match.params.username) ||
            userIsGlobalAdmin
        if (this.state.loading) {
            return this.renderLoadingSkeleton()
        } else {
            return (
                <>
                    <Fieldset title="General info">
                        <Grid.Row>
                            <Grid.Col>
                                <FormGroup>
                                    <Input
                                        {...this.getCommonInputProps(
                                            'firstName'
                                        )}
                                        label="First Name"
                                        required
                                        disabled={disabledFields}
                                    />
                                </FormGroup>
                            </Grid.Col>
                            <Grid.Col>
                                <FormGroup>
                                    <Input
                                        {...this.getCommonInputProps(
                                            'lastName'
                                        )}
                                        label="Last Name"
                                        required
                                        disabled={disabledFields}
                                    />
                                </FormGroup>
                            </Grid.Col>
                            <Grid.Col>
                                <FormGroup>
                                    <Input
                                        {...this.getCommonInputProps('email')}
                                        label="Email"
                                        required
                                        disabled={disabledFields}
                                    />
                                </FormGroup>
                            </Grid.Col>
                        </Grid.Row>
                        {displayUsernameField ? (
                            <Grid.Row>
                                <Grid.Col lg={4}>
                                    <FormGroup>
                                        <Input
                                            {...this.getCommonInputProps(
                                                'username'
                                            )}
                                            label="Username"
                                            required
                                        />
                                    </FormGroup>
                                </Grid.Col>
                            </Grid.Row>
                        ) : null}
                        {values.localUser && userIsAdmin ? (
                            <Grid.Row>
                                <Grid.Col lg={6}>
                                    <FormGroup>
                                        <Input
                                            {...this.getCommonInputProps(
                                                'password'
                                            )}
                                            label="Password"
                                            type="password"
                                            disabled={disabledFields}
                                        />
                                    </FormGroup>
                                </Grid.Col>
                                <Grid.Col lg={6}>
                                    <FormGroup>
                                        <Input
                                            {...this.getCommonInputProps(
                                                'repeatedPassword'
                                            )}
                                            label="Repeat password"
                                            type="password"
                                            disabled={disabledFields}
                                        />
                                    </FormGroup>
                                </Grid.Col>
                            </Grid.Row>
                        ) : null}
                        <Grid.Row>
                            <Grid.Col className={'switch'} lg={4}>
                                <FormGroup>
                                    <Checkbox
                                        {...this.getCommonCheckboxProps(
                                            'locked'
                                        )}
                                        type="switch"
                                        label="Locked"
                                        required
                                        disabled={disabledFields}
                                    />
                                </FormGroup>
                            </Grid.Col>
                            {!this.props.match.params.username && (
                                <Grid.Col className={'switch'} lg={4}>
                                    <FormGroup>
                                        <Checkbox
                                            {...this.getCommonCheckboxProps(
                                                'localUser'
                                            )}
                                            type="switch"
                                            label="Local User"
                                            required
                                            disabled={disabledFields}
                                        />
                                    </FormGroup>
                                </Grid.Col>
                            )}
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Col>
                                <Button
                                    className={'mb-3'}
                                    size={'sm'}
                                    key="submit"
                                    variant="secondary"
                                    onClick={this.submitGeneralInfo}
                                    disabled={disabledFields}
                                >
                                    Submit
                                </Button>
                            </Grid.Col>
                        </Grid.Row>
                    </Fieldset>
                    <Fieldset title="Groups">
                        <Info
                            info={
                                'You need to first submit the general info in order to be able to link the user to a group'
                            }
                        />
                        {(this.state.userCreated ||
                            this.props.match.params.username) && (
                            <Grid.Row>
                                <Grid.Col lg={6}>
                                    <FormGroup>
                                        <AsyncSelect
                                            {...this.getCommonSelectProps(
                                                'groups'
                                            )}
                                            apiConfig={{
                                                url:
                                                    '/api/auth/tenants/' +
                                                    this.getUserOrganization() +
                                                    '/groups',
                                                params: inputValue => ({
                                                    page: 1,
                                                    name_like: inputValue,
                                                    sort: '+name',
                                                }),
                                            }}
                                            itemRenderer={
                                                this.handleGroupsRender
                                            }
                                            label="Groups"
                                            search
                                            isMulti
                                            dependencies={[
                                                authenticatedUserStore.selectedOrganization,
                                            ]}
                                        />
                                    </FormGroup>
                                </Grid.Col>
                            </Grid.Row>
                        )}
                        {(this.state.userCreated ||
                            this.props.match.params.username) && (
                            <Grid.Row>
                                <Grid.Col>
                                    <Button
                                        className={'mb-3'}
                                        size={'sm'}
                                        key="submit"
                                        variant="secondary"
                                        onClick={this.submitGroupInfo}
                                    >
                                        Submit
                                    </Button>
                                </Grid.Col>
                            </Grid.Row>
                        )}
                    </Fieldset>
                </>
            )
        }
    }
}

export default observer(UserForm)
